import React, { forwardRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import '../styles/menu.css';
import { useCounters } from '../../hooks/useCounters';
import { fetchNotifications } from '../../store/slices/countersSlice';
import NotificationBadge from './NotificationBadge';
import {
  hydrateInventoryNew,
  loadInventoryNewCount,
  resetNewInventoryItems,
  saveInventoryNewCount,
} from '../../features/inventory/store/inventoryNewSlice';

import { ReactComponent as TasksIcon } from '../../assets/menu/tasks.svg';
import { ReactComponent as InventoryIcon } from '../../assets/menu/inventory.svg';
import { ReactComponent as RollIcon } from '../../assets/menu/roll.svg';
import { ReactComponent as PlayIcon } from '../../assets/menu/play.svg';
import { ReactComponent as NetworkIcon } from '../../assets/menu/network.svg';
import { ReactComponent as ShopIcon } from '../../assets/menu/shop.svg';
import { useTranslation } from 'react-i18next';

const menuLabel = (t, key) => ({
  Tasks: t('menu.tasks', 'Tasks'),
  Inventory: t('menu.inventory', 'Inventory'),
  Roll: t('menu.roll', 'Roll'),
  Play: t('menu.play', 'Play'),
  Network: t('menu.network', 'Network'),
  Shop: t('menu.shop', 'Shop'),
}[key]);

const tabs = [
  { key: 'Tasks', icon: TasksIcon, label: 'Tasks', path: '/tasks' },
  { key: 'Inventory', icon: InventoryIcon, label: 'Inventory', path: '/inventory' },
  { key: 'Roll', icon: RollIcon, label: 'Roll', path: '/lucky-spin' },
  // Missions & Lair; nested game routes (/lair/match3/:runId, ...) keep this tab highlighted
  { key: 'Play', icon: PlayIcon, label: 'Play', path: '/lair', prefix: true },
  { key: 'Network', icon: NetworkIcon, label: 'Network', path: '/friends' },
  { key: 'Shop', icon: ShopIcon, label: 'Shop', path: '/shop' },
];

const Menu = forwardRef(({ activeTab, onTabClick }, refs) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { tasks, shop, network, shouldShowBadge } = useCounters();
  const userId = useSelector((s) => s.user?.data?.id);
  const inventoryNew = useSelector((s) => s.inventoryNew);
  const newItems = inventoryNew?.count || 0;
  const onInventory = location.pathname.startsWith('/inventory');

  // Badges: tasks = claimable achievements, inventory = openable chests (chest + matching key).
  // Re-fetch GET /v1/notifications on every tab change so counts never go stale
  // even when the WebSocket push is unavailable.
  useEffect(() => {
    dispatch(fetchNotifications());
  }, [location.pathname, dispatch]);

  // Inventory badge is client-side: items received since the last visit (inventoryNewSlice),
  // persisted per user in localStorage. Being on /inventory = everything is seen → reset,
  // including items that arrive while the player is there (chest opening, Merge board).
  useEffect(() => {
    if (userId) dispatch(hydrateInventoryNew({ userId, saved: loadInventoryNewCount(userId) }));
  }, [userId, dispatch]);

  useEffect(() => {
    if (onInventory && newItems > 0) dispatch(resetNewInventoryItems());
  }, [onInventory, newItems, dispatch]);

  useEffect(() => {
    if (inventoryNew?.userId && inventoryNew.userId === userId) saveInventoryNewCount(userId, inventoryNew.count);
  }, [inventoryNew, userId]);

  // Маппинг счетчиков для каждой вкладки
  const getCounterForTab = (key) => {
    switch (key) {
      case 'Inventory': return newItems;
      case 'Tasks': return tasks;
      case 'Shop': return shop;
      case 'Network': return network;
      default: return 0;
    }
  };

  return (
    <div className="menu-bar">
      {tabs.map(({ key, icon: Icon, label, path, prefix }) => {
        const isActive = location.pathname === path || (prefix && location.pathname.startsWith(`${path}/`));
        const ref = refs?.[key.toLowerCase()];
        const counter = getCounterForTab(key);
        const showBadge = key === 'Inventory' ? newItems > 0 : shouldShowBadge(key.toLowerCase());

        return (
          <div
            key={key}
            className={`menu-item ${isActive ? 'active' : ''}`}
            onClick={() => { navigate(path)}}
            ref={ref}
            data-reward-target={key === 'Inventory' ? 'inventory' : undefined}
            data-tutorial={`menu-${key.toLowerCase()}`}
          >
            <div className="icon-wrapper">
              <Icon className="menu-icon" />
              {showBadge && (
                <NotificationBadge count={counter} />
              )}
            </div>
            <div className="menu-label">{menuLabel(t, key) || label}</div>
          </div>
        );
      })}
    </div>
  );
});

export default Menu;